/** @typedef {import('./playerTypes').MatchPlayerRef} MatchPlayerRef */

export const MATCH_PLAYER_PLACEHOLDER_ID = "unassigned";

/**
 * @param {{ playerId?: string | null; playerName?: string | null; legacyName?: string | null }} fields
 * @returns {MatchPlayerRef}
 */
export function toMatchPlayerRef(fields) {
  const playerId = (fields.playerId || "").trim() || null;
  const legacy =
    (fields.playerName || fields.legacyName || "").trim() || null;
  return { playerId, playerName: playerId ? null : legacy };
}

/**
 * Follow merge chain in memory (repository also resolves on read).
 * @param {import('./playerTypes').PlayerRecord | null | undefined} player
 * @param {(id: string) => import('./playerTypes').PlayerRecord | null | undefined} getById
 * @param {number} [maxHops]
 * @returns {import('./playerTypes').PlayerRecord | null}
 */
export function resolvePlayerChain(player, getById, maxHops = 8) {
  if (!player) return null;
  let current = player;
  let hops = 0;
  while (current.isMerged && current.mergedIntoPlayerId && hops < maxHops) {
    const next = getById(current.mergedIntoPlayerId);
    if (!next) break;
    current = next;
    hops += 1;
  }
  return current.isActive === false ? null : current;
}

/**
 * @param {MatchPlayerRef} ref
 * @param {(id: string) => Promise<import('./playerTypes').PlayerRecord | null>} resolveById
 * @returns {Promise<{ playerId: string | null; displayName: string; legacyName: string | null }>}
 */
export async function resolveMatchPlayerRef(ref, resolveById) {
  if (ref.playerId) {
    const player = await resolveById(ref.playerId);
    if (player) {
      return {
        playerId: player.playerId,
        displayName: player.displayName,
        legacyName: null,
      };
    }
  }
  const legacy = ref.playerName || "";
  return {
    playerId: ref.playerId,
    displayName: legacy,
    legacyName: legacy || null,
  };
}

/**
 * Prefer explicit player IDs on live teams; fall back to legacy team name strings.
 * @param {{ teamA?: { slots?: unknown[]; players?: unknown[]; playerIds?: string[]; name?: string }; teamB?: { slots?: unknown[]; players?: unknown[]; playerIds?: string[]; name?: string } }} livePayload
 */
export function pickMatchPlayerRefsFromLive(livePayload) {
  const sideA = livePayload?.teamA;
  const sideB = livePayload?.teamB;

  const firstPlayerId = (team) => {
    const slots = team?.slots;
    if (Array.isArray(slots) && slots[0]?.playerId) {
      return String(slots[0].playerId).trim();
    }
    const ids = team?.playerIds;
    if (Array.isArray(ids) && ids[0]) return String(ids[0]).trim();
    const players = team?.players;
    if (Array.isArray(players)) {
      for (const p of players) {
        if (p && typeof p === "object" && p.playerId) return String(p.playerId).trim();
        if (typeof p === "string" && p.startsWith("player_")) return p.trim();
      }
    }
    return null;
  };

  const legacyName = (team) => (team?.name || "").trim() || null;

  const aId = firstPlayerId(sideA);
  const bId = firstPlayerId(sideB);

  return {
    player1Id: aId || legacyName(sideA) || MATCH_PLAYER_PLACEHOLDER_ID,
    player2Id: bId || legacyName(sideB) || MATCH_PLAYER_PLACEHOLDER_ID,
    player1Name: aId ? null : legacyName(sideA),
    player2Name: bId ? null : legacyName(sideB),
  };
}
